import React from 'react'
import CustomModal from './CustomModal';
import CustomInput from './CustomInput';
import { Button, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as yup from "yup";
import CustomButton from './CustomButton';
import { useDispatch } from 'react-redux';
import { createAssessmentWithAI } from '../store/assessmentsSlice';
import { useNavigate } from 'react-router-dom';

const CreateAssessmentWithAIForm = ({ isCreateAssessmentAIOpen, setIsCreateAssessmentAIOpen }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Create a custom validation method to check for leading or trailing whitespace
  yup.addMethod(yup.string, 'noLeadingTrailingWhitespace', function () {
    return this.transform((value) => (typeof value === 'string' ? value.trim() : value)) // Trim the value
      .test(
        'startsWithNonWhitespace',
        'Whitespace is not allowed',
        value => value && value.trim().length > 0 // Value should have at least one non-whitespace character
      );
  });

  const validationSchema = yup.object({
    assessment_name: yup
      .string("Assessment name should be a string")
      .required("Assessment name is required")
      .noLeadingTrailingWhitespace()  // Check for leading or trailing whitespace
      .max(100, "Assessment name cannot be more than 100 characters"),  // Minimum length example
    assessment_description: yup
      .string("Description should be a string")
      .required("Assessment description is required")
      .noLeadingTrailingWhitespace()  // Check for leading or trailing whitespace
      .max(1000, "Description cannot be more than 1000 characters"),  // Minimum length example
    passing_perc: yup
      .number("Passing percentage must be a number")
      .required("Passing percentage is required")
      .min(0, "Passing percentage cannot be less than 0")
      .max(100, "Passing percentage cannot be more than 100"),  // Percentage validation
    role: yup
      .string("Role must be a string")
      .required("Role is required")
      .noLeadingTrailingWhitespace()  // Check for leading or trailing whitespace
      .min(1, "Role must be at least 1 characters") // Example length validation
      .max(100, "Role cannot be more than 100 characters"),  // Percentage validation
    experience_in_yrs: yup
      .number("Experience must be a number")
      .required("Experience in years is required")
      .min(0, "Experience must be at least 0 years")  // Minimum experience of 0 years
      .max(25, "Experience cannot be more than 25 years"),  // Percentage validation
    job_desription: yup
      .string("Job description should be a string")
      .required("Job description is required")
      .noLeadingTrailingWhitespace()  // Check for leading or trailing whitespace
      .min(1, "Job description must be at least 1 characters")  // Minimum length
      .max(1500, "Job description cannot be more than 1500 characters"),  // Percentage validation
    skills: yup
      .string("Skills should be a string")
      .required("Skills are required")
      .noLeadingTrailingWhitespace()  // Check for leading or trailing whitespace
      .min(3, "Skills must be at least 3 characters")  // Minimum length
      .max(256, "Skills cannot be more than 256 characters"),  // Percentage validation
    question_number: yup
      .number("Number of questions must be a number")
      .required("Number of questions is required")
      .positive("Number of questions must be a positive integer")  // Positive integer validation
      .integer("Number of questions must be an integer")  // Ensure it's an integer
      .min(1, "Number of questions must be at least 1")
      .max(100, "Number of questions cannot be more than 30")
  });

  const createAssessmentWithAIFormik = useFormik({
    initialValues: {
      assessment_name: "",
      assessment_description: '',
      passing_perc: '',
      role: '',
      experience_in_yrs: '',
      job_desription: '',
      skills: '',
      question_number: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        const response = await dispatch(createAssessmentWithAI({ ...values })).unwrap();
        setIsCreateAssessmentAIOpen(false);
        toast.success('Assessment created successfully');        
        if (response?.data?.assessment_id) navigate(`/admin/assessment/${response?.data?.assessment_id}`);
      } catch (error) {
        console.log(error, "error");
      }
    }
  });

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
  } = createAssessmentWithAIFormik;

  const handleShareClose = () => {
    setIsCreateAssessmentAIOpen(false);
  };

  return (
    <CustomModal show={isCreateAssessmentAIOpen} size="lg"
      onHide={handleShareClose} modalTitle="Create Assessment With AI">
      <CustomInput name="assessment_name" required label="Assessment Name" type="textarea" placeholder="Enter assessment name" error={touched?.assessment_name && Boolean(errors?.assessment_name)} errorText={touched?.assessment_name && errors?.assessment_name} onChange={handleChange} onBlur={handleBlur} value={values.assessment_name} />
      <CustomInput name="assessment_description" rows='2' required label="Assessment Description" type="textarea" placeholder="Enter assessment description" error={touched?.assessment_description && Boolean(errors?.assessment_description)} errorText={touched?.assessment_description && errors?.assessment_description} onChange={handleChange} onBlur={handleBlur} value={values.assessment_description} />
      <CustomInput name="passing_perc" min={0} max={100} required label="Passing Percentage" type="number" placeholder="Enter passing percentage" error={touched?.passing_perc && Boolean(errors?.passing_perc)} errorText={touched?.passing_perc && errors?.passing_perc} onChange={handleChange} onBlur={handleBlur} value={values.passing_perc} />
      <CustomInput name="role" label="Role" required type="textarea" placeholder="Enter role" error={touched?.role && Boolean(errors?.role)} errorText={touched?.role && errors?.role} onChange={handleChange} onBlur={handleBlur} value={values.role} />
      <CustomInput name="experience_in_yrs" min={0} max={25} required label="Experience In Years" type="number" placeholder="Enter experience in years" error={touched?.experience_in_yrs && Boolean(errors?.experience_in_yrs)} errorText={touched?.experience_in_yrs && errors?.experience_in_yrs} onChange={handleChange} onBlur={handleBlur} value={values.experience_in_yrs} />
      <CustomInput name="job_desription" rows='2' required label="Job Desription" type="textarea" placeholder="Enter job desription" error={touched?.job_desription && Boolean(errors?.job_desription)} errorText={touched?.job_desription && errors?.job_desription} onChange={handleChange} onBlur={handleBlur} value={values.job_desription} />
      <CustomInput name="skills" rows='2' required label="Skills" type="textarea" placeholder="Enter skills" error={touched?.skills && Boolean(errors?.skills)} errorText={touched?.skills && errors?.skills} onChange={handleChange} onBlur={handleBlur} value={values.skills} />
      <CustomInput name="question_number" min={1} max={30} required label="Question Number" type="number" placeholder="Enter question number" error={touched?.question_number && Boolean(errors?.question_number)} errorText={touched?.question_number && errors?.question_number} onChange={handleChange} onBlur={handleBlur} value={values.question_number} />
      <Modal.Footer>
        <Button variant="secondary" onClick={handleShareClose}>
          Close
        </Button>
        <CustomButton variant="primary" minWidth="90px" disabled={isSubmitting} onClick={handleSubmit} name="Save" isLoading={isSubmitting} />
      </Modal.Footer>
    </CustomModal>)
}

export default CreateAssessmentWithAIForm